import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Segmented, Button, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  buildStaffProfessionalSaveBody,
  removeNursingSchoolRow,
  upsertNursingSchoolRow,
  useLicenseAuthorities,
  useSaveStaffProfessionalDetails,
  useStaffProfessionalDetails,
} from '@/settings/services';
import type {
  StaffNursingSchoolRow,
  StaffProfessionalDetails,
} from '@/types/settings-api';
import { NursingSchoolEditDialog } from '../professional/nursing-school-edit-dialog';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Professional Details tab — PROD-765 + PROD-788 (nurse-only).
 *
 * Flutter source: `staff_professional_tab.dart`.
 *
 * Three sections:
 *   1. PROFESSIONAL DETAILS — license number / authority / expiration date.
 *      Authority is a chip-picker over master `useLicenseAuthorities`
 *      (7 UAE bodies — DHA / DHCC / DOH / CDA / EHA / HAAD / MOH).
 *   2. NURSING SCHOOL — list of qualifications. Add / Edit / Remove via
 *      `<NursingSchoolEditDialog>`. Diff vs the loaded source is computed
 *      by `buildStaffProfessionalSaveBody` at save time.
 *   3. (page chrome) Save — POSTs the full wrapper as a DIFF (see
 *      `docs/CONVERSIONS/settings-staff-professional-details/DEV_CURL.md`
 *      §5 for the contract).
 *
 * All wire shapes captured 2026-05-24/25. The save handler is registered
 * with the page chrome via a tab-save callback prop so the existing
 * page-level Save button dispatches THIS tab's POST instead of the
 * default `useUpdateStaff` when this tab is active. Same registration
 * pattern as Practitioner's `PractitionerSaveContext` (PR #340) but the
 * Staff tab is a callback-prop variant — see `staff-edit-page.tsx`.
 */
export interface StaffProfessionalTabProps {
  staffSetupId: string | null;
  canEdit: boolean;
  /**
   * Called when the tab's draft becomes dirty, so the page can override
   * its chrome Save button to dispatch THIS tab's mutation. Pass `null`
   * to clear the override (tab unmounts).
   */
  onRegisterSave?: (handle: TabSaveHandle | null) => void;
}

/**
 * Handle the tab exposes to the page chrome so the chrome Save button
 * can dispatch the tab's save when active. Mirrors
 * `PractitionerSaveHandle` (PR #340).
 */
export interface TabSaveHandle {
  canSave: boolean;
  isPending: boolean;
  save: () => void;
  label?: string;
}

export function StaffProfessionalTab({
  staffSetupId,
  canEdit,
  onRegisterSave,
}: StaffProfessionalTabProps) {
  const { data: source, isLoading } = useStaffProfessionalDetails(staffSetupId);
  const { data: authorities = [], isLoading: authoritiesLoading } =
    useLicenseAuthorities();
  const saveMutation = useSaveStaffProfessionalDetails(staffSetupId);

  const [draft, setDraft] = useState<StaffProfessionalDetails | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Track the edit POSITION, not the row object. PROD-1206: every unsaved row
  // has `id === null`, so anything keyed on `id` cannot tell two of them apart.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const editingRow =
    editingIndex === null ? null : (draft?.nursingSchool[editingIndex] ?? null);

  // Re-hydrate draft whenever the server-canonical wrapper changes
  // (initial load + post-save invalidate).
  useEffect(() => {
    if (source) setDraft(source);
  }, [source]);

  const isDirty = useMemo(() => {
    if (!source || !draft) return false;
    return JSON.stringify(source) !== JSON.stringify(draft);
  }, [source, draft]);

  // Required-field gate. License number + authority + expiration date.
  // Nursing-school list is optional (can be empty).
  const isValid =
    !!draft &&
    draft.professionalDetails.licenseNumber.trim().length > 0 &&
    draft.professionalDetails.licenseIssuingAuthority.length > 0 &&
    draft.professionalDetails.licenseExpirationDate.length > 0;

  const handleSave = useCallback(() => {
    if (!draft || !source || !staffSetupId) return;
    if (!isValid) {
      toast.error('License number, authority, and expiration date are required.');
      return;
    }
    const body = buildStaffProfessionalSaveBody({
      staffId: staffSetupId,
      source,
      draft,
    });
    saveMutation.mutate(body, {
      onSuccess: () => toast.success('Professional details saved'),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Save failed'),
    });
  }, [draft, source, staffSetupId, isValid, saveMutation]);

  // Register / unregister save handle with the page chrome. Called
  // unconditionally before any early-return — Rules of Hooks.
  useEffect(() => {
    if (!onRegisterSave) return;
    if (canEdit && draft) {
      onRegisterSave({
        canSave: isDirty && isValid,
        isPending: saveMutation.isPending,
        save: handleSave,
        label: saveMutation.isPending
          ? 'Saving…'
          : 'Save Professional Details',
      });
    } else {
      onRegisterSave(null);
    }
    return () => onRegisterSave(null);
  }, [
    onRegisterSave,
    canEdit,
    draft,
    isDirty,
    isValid,
    saveMutation.isPending,
    handleSave,
  ]);

  if (!staffSetupId) {
    return (
      <p className="text-sm text-text-muted">
        Save staff details first to manage professional information.
      </p>
    );
  }
  if (isLoading || !draft) {
    return <BrandLoading size={48} label="Loading…" />;
  }

  const setBlockField = (
    key: keyof StaffProfessionalDetails['professionalDetails'],
    value: string,
  ) => {
    setDraft({
      ...draft,
      professionalDetails: { ...draft.professionalDetails, [key]: value },
    });
  };

  const openAddDialog = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const openEditDialog = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  // PROD-1817 regression fix. The port replaced this with PM's id-keyed
  // version, reintroducing the exact defect PROD-1206 fixed: `next.id === null`
  // meant "add", so EDITING a not-yet-saved row appended a duplicate instead of
  // replacing it (every unsaved row shares `id === null`). Removal was worse —
  // `if (!editingRow?.id) return` made an unsaved row impossible to delete.
  // Both now go through the position-keyed helpers restored alongside them.
  const handleRowSave = (next: StaffNursingSchoolRow) => {
    setDraft({
      ...draft,
      nursingSchool: upsertNursingSchoolRow(draft.nursingSchool, next, editingIndex),
    });
  };

  const handleRowRemove = () => {
    setDraft({
      ...draft,
      nursingSchool: removeNursingSchoolRow(draft.nursingSchool, editingIndex),
    });
  };

  return (
    <div className="space-y-6">
      <h2 className={SETTINGS_SECTION_TITLE}>
        Professional Details
      </h2>
      <PlainCard>
        <div>
          <FieldLabel stacked>
            License number <span className="text-status-error">*</span>
          </FieldLabel>
          <TextField
            value={draft.professionalDetails.licenseNumber}
            maxLength={50}
            placeholder="Enter license number"
            disabled={!canEdit}
            onChange={(e) => setBlockField('licenseNumber', e.target.value)}
          />
        </div>

        <div>
          <FieldLabel stacked>
            License issuing authority <span className="text-status-error">*</span>
          </FieldLabel>
          {authoritiesLoading ? (
            <BrandLoading size={48} label="Loading authorities…" />
          ) : (
            <Segmented
              aria-label="License issuing authority"
              // Master-data single-select. Field may be empty pre-selection
              // (it's required but starts blank on a new staff record), so an
              // unmatched value renders no active segment — pass undefined
              // rather than '' to keep Segmented's value contract clean.
              value={
                draft.professionalDetails.licenseIssuingAuthority || undefined
              }
              onChange={(v) => setBlockField('licenseIssuingAuthority', v)}
              options={authorities.map((a) => ({
                value: a.Id,
                label: a.Name,
                disabled: !canEdit,
              }))}
            />
          )}
        </div>

        <div>
          <FieldLabel stacked>
            License expiration date <span className="text-status-error">*</span>
          </FieldLabel>
          <DatePickerField
            value={toDateInputValue(
              draft.professionalDetails.licenseExpirationDate,
            )}
            disabled={!canEdit}
            onChange={(v) =>
              setBlockField(
                'licenseExpirationDate',
                v ? `${v}T00:00:00.000` : '',
              )
            }
          />
        </div>
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Nursing School
      </h2>
      <div data-slot="settings-school-card" className="overflow-hidden rounded-lg border border-border-hairline bg-surface-card shadow-1">
        {draft.nursingSchool.length === 0 ? (
          <p className="px-6 py-4 text-sm text-text-muted">
            No nursing schools added yet.
          </p>
        ) : (
          <ul className="divide-y divide-border-hairline">
            {draft.nursingSchool.map((row, idx) => (
              <li
                key={row.id ?? `new-${idx}`}
                className="flex items-center justify-between px-6 py-3"
              >
                <div>
                  <p data-slot="settings-record-title" className="text-sm font-semibold text-text-default">
                    {row.university.MedicalSchool || 'Untitled school'}
                  </p>
                  <p className="text-sm text-text-muted">
                    {row.degree.code.displayName ??
                      row.degree.code.code ??
                      'Degree'}{' '}
                    · {row.passingYear}
                  </p>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label="Edit nursing school"
                      className="icon-action"
                      onClick={() => openEditDialog(idx)}
                    >
                      <Pencil className="size-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      aria-label="Remove nursing school"
                      className="icon-action"
                      onClick={() => {
                        setDraft({
                          ...draft,
                          nursingSchool: removeNursingSchoolRow(
                            draft.nursingSchool,
                            idx,
                          ),
                        });
                      }}
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <div className="border-t border-border-hairline p-2">
            <Button
              type="button"
              variant="tertiary"
              data-slot="settings-add"
              data-block=""
              className="w-full text-brand-primary hover:bg-blue-100"
              onClick={openAddDialog}
            >
              <Plus className="size-4 mr-2" />
              Add Nursing School
            </Button>
          </div>
        )}
      </div>

      <NursingSchoolEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editingRow}
        onSave={handleRowSave}
        onRemove={editingRow ? handleRowRemove : undefined}
      />
    </div>
  );
}

function PlainCard({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-6 py-6 flex flex-col gap-6">
      {children}
    </section>
  );
}

/** No-Z ISO → `YYYY-MM-DD` for the native <input type="date"> binding. */
function toDateInputValue(noZIso: string): string {
  if (!noZIso) return '';
  const t = noZIso.indexOf('T');
  return t >= 0 ? noZIso.slice(0, t) : noZIso;
}
